import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import api from '../../services/api';
import {
  BROWN, Section, Stat, Tableau, FicheLien, Statut, fmtMt, fmtNb, fmtDH, fmtL, fmtH, fmtDateFr,
} from './components/commun';

// Fiche détaillée d'une entité (lien depuis les listes du dashboard) : /fiche/:type/:id?start=&end=
// type = materiel | ouvrier | citerne | chantier. Lecture seule, liens croisés entre fiches.

const dt = (v) => (v ? fmtDateFr(String(v).slice(0, 10)) : '—');
const dec = (v) => (v == null ? '—' : String(v).replace('.', ','));
const TITRES = { materiel: '🚜 Matériel', ouvrier: '👷 Ouvrier', citerne: '⛽ Citerne', chantier: '🏗 Chantier' };

const Infos = ({ items }) => (
  <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: '0.88rem' }}>
    {items.filter(([, v]) => v !== undefined).map(([k, v]) => (
      <div key={k}><span className="text-muted">{k} : </span><strong>{v === '' || v == null ? '—' : v}</strong></div>
    ))}
  </div>
);

const FicheMateriel = ({ d, periode }) => {
  const i = d.infos, s = d.stats;
  return (
    <>
      <Infos items={[
        ['Type', i.type], ['Marque', i.marque], ['Société', i.societe], ['État', i.etat],
        ['Chantier', <FicheLien type="chantier" id={i.chantierId} periode={periode}>{i.chantier}</FicheLien>],
        ['Chauffeur', i.chauffeur], ['Compteur', i.compteur != null ? `${fmtNb(i.compteur)} ${i.modeCompteur}` : '—'],
      ]} />
      <div className="row">
        <Stat label="Jours pointés" value={fmtNb(s.joursPointes)} />
        <Stat label="Heures" value={fmtH(s.heures)} />
        <Stat label="Gasoil" value={fmtL(s.litres)} sub={s.litresHeure != null ? `${dec(s.litresHeure)} L/h` : null} />
        <Stat label="Écarts compteur" value={fmtNb(s.ecarts)} alert={s.ecarts > 0} />
        <Stat label="Réparations (période)" value={fmtDH(s.reparations)} />
      </div>
      <div className="row">
        <div className="col-12 col-lg-5">
          <Section titre="🛢 Vidange">
            <Infos items={[
              ['Statut', <Statut s={i.statutVidange} />], ['Depuis la vidange', `${fmtNb(i.cycle)} / ${fmtNb(i.interval)} ${i.modeCompteur}`],
              ['Dernière vidange', dt(i.dateDerniereVidange)],
            ]} />
            <Tableau rows={d.vidanges} vide="Aucune vidange enregistrée" columns={[
              { key: 'date', label: 'Date', fmt: dt }, { key: 'compteur', label: 'Compteur', num: true, fmt: fmtNb },
              { key: 'mode', label: 'Unité' }, { key: 'observation', label: 'Observation' },
            ]} />
          </Section>
        </div>
        <div className="col-12 col-lg-7">
          <Section titre="📄 Papiers">
            <Tableau rows={i.papiers.map((p, k) => ({ ...p, id: k }))} columns={[
              { key: 'libelle', label: 'Papier' }, { key: 'date', label: 'Échéance', fmt: dt },
              { key: 'jours', label: 'Jours restants', num: true, fmt: v => (v == null ? '—' : v) },
              { key: 'statut', label: 'Statut', render: p => <Statut s={p.statut} /> },
            ]} />
          </Section>
        </div>
      </div>
      <Section titre="📋 Pointages de la période">
        <Tableau rows={d.pointages} total maxHeight={420} columns={[
          { key: 'date', label: 'Date', fmt: dt },
          { key: 'chantier', label: 'Chantier', render: r => <FicheLien type="chantier" id={r.chantierId} periode={periode}>{r.chantier}</FicheLien> },
          { key: 'heures', label: 'Heures', num: true, total: true, fmt: fmtNb }, { key: 'litres', label: 'Gasoil (L)', num: true, total: true, fmt: fmtNb },
          { key: 'compteurPrec', label: 'Compteur avant', num: true }, { key: 'compteurNv', label: 'Compteur après', num: true },
          { key: 'message', label: 'Écart', render: r => (Number(r.ecart) === 1 ? <span style={{ color: '#c62828' }}>{r.message || 'Écart'}</span> : '') },
        ]} />
      </Section>
      <Section titre="🔧 Réparations (100 dernières)">
        <Tableau rows={d.reparations} total columns={[
          { key: 'date', label: 'Date', fmt: dt }, { key: 'refBdc', label: 'Réf. BDC' }, { key: 'designation', label: 'Désignation' },
          { key: 'fournisseur', label: 'Fournisseur' }, { key: 'qte', label: 'Qté', num: true },
          { key: 'prix', label: 'Prix', num: true, fmt: fmtMt }, { key: 'montant', label: 'Montant (DH)', num: true, total: true, fmt: fmtMt },
        ]} />
      </Section>
    </>
  );
};

const FicheOuvrier = ({ d, periode }) => {
  const i = d.infos, s = d.stats;
  return (
    <>
      <Infos items={[
        ['Qualification', i.qualification], ['Taux journalier', fmtDH(i.taux)], ['Société', i.societe], ['Matricule Sage', i.matriculeSage],
        ['Chantier d\'affectation', <FicheLien type="chantier" id={i.chantierId} periode={periode}>{i.chantier}</FicheLien>],
        ['Entrée', dt(i.dateEntree)], ['STC', i.dateStc ? dt(i.dateStc) : undefined], ['Statut', i.bloque ? 'Bloqué' : 'Actif'],
      ]} />
      <div className="row">
        <Stat label="Jours pointés" value={fmtNb(s.joursPointes)} sub={`${fmtNb(s.jours)} j · ${fmtNb(s.heuresSup)} h sup`} />
        <Stat label="Montant" value={fmtDH(s.montant)} info="Taux appliqué × (jours + heures sup / 10)." />
        <Stat label="Chantiers" value={fmtNb(s.chantiers.length)} sub={s.chantiers.join(', ')} />
      </div>
      <Section titre="📋 Pointages de la période">
        <Tableau rows={d.pointages} total maxHeight={520} columns={[
          { key: 'date', label: 'Date', fmt: dt },
          { key: 'chantier', label: 'Chantier', render: r => <FicheLien type="chantier" id={r.chantierId} periode={periode}>{r.chantier}</FicheLien> },
          { key: 'jours', label: 'Jours', num: true, total: true, fmt: fmtNb }, { key: 'heures', label: 'Heures sup', num: true, total: true, fmt: fmtNb },
          { key: 'taux', label: 'Taux', num: true, fmt: fmtMt }, { key: 'montant', label: 'Montant (DH)', num: true, total: true, fmt: fmtMt },
          { key: 'datePaiement', label: 'Payé le', fmt: v => (v ? dt(v) : '') },
        ]} />
      </Section>
    </>
  );
};

const FicheCiterne = ({ d, periode }) => {
  const i = d.infos, s = d.stats;
  return (
    <>
      <Infos items={[
        ['Référence', i.reference], ['Emplacement', i.emplacement],
        ['Chantier', <FicheLien type="chantier" id={i.chantierId} periode={periode}>{i.chantier}</FicheLien>],
        ['Capacité', `${fmtL(i.min)} min · ${fmtL(i.max)} max`], ['Active', i.actif ? 'Oui' : 'Non'],
      ]} />
      <div className="row">
        <Stat label="Stock actuel" value={fmtL(i.stock)} alert={i.stock <= i.min} sub={i.max ? `${Math.round((i.stock * 100) / i.max)} % de la capacité` : null} />
        <Stat label="Livré (période)" value={fmtL(s.entrees)} />
        <Stat label="Sorti (période)" value={fmtL(s.sorties)} />
        <Stat label="Ajustements" value={fmtL(s.ajustements)} />
      </div>
      <Section titre="🔁 Mouvements de la période">
        <Tableau rows={d.mouvements} maxHeight={520} columns={[
          { key: 'date', label: 'Date', fmt: dt }, { key: 'type', label: 'Type' },
          { key: 'litres', label: 'Litres', num: true, fmt: fmtNb },
          { key: 'matricule', label: 'Matériel', render: r => (r.materielId ? <FicheLien type="materiel" id={r.materielId} periode={periode}>{r.matricule}</FicheLien> : '') },
          { key: 'commentaire', label: 'Commentaire' },
        ]} />
      </Section>
    </>
  );
};

const FicheChantier = ({ d, periode }) => {
  const i = d.infos, s = d.stats;
  return (
    <>
      <Infos items={[['Société', i.societe], ['Localisation', i.location]]} />
      <div className="row">
        <Stat label="Ouvriers" value={fmtNb(s.ouvriers)} sub={`${fmtNb(s.jours)} jours`} />
        <Stat label="Masse salariale" value={fmtDH(s.masse)} />
        <Stat label="Matériels" value={fmtNb(s.materiels)} sub={fmtH(s.heures)} />
        <Stat label="Gasoil" value={fmtL(s.litres)} />
        <Stat label="Réparations" value={fmtDH(s.reparations)} />
      </div>
      {d.alertes.length > 0 && (
        <Section titre="🔔 Alertes des matériels affectés">
          <Tableau rows={d.alertes} columns={[
            { key: 'matricule', label: 'Matricule', render: r => <FicheLien type="materiel" id={r.materielId} periode={periode}>{r.matricule}</FicheLien> },
            { key: 'type', label: 'Type' }, { key: 'vidange', label: 'Vidange', render: r => <Statut s={r.vidange} /> },
            { key: 'papiers', label: 'Papiers', render: r => <Statut s={r.papiers} /> },
          ]} />
        </Section>
      )}
      <div className="row">
        <div className="col-12 col-xl-6">
          <Section titre="👷 Ouvriers pointés">
            <Tableau rows={d.ouvriers} total maxHeight={460} columns={[
              { key: 'nom', label: 'Ouvrier', render: r => <FicheLien type="ouvrier" id={r.ouvrierId} periode={periode}>{r.nom}</FicheLien> },
              { key: 'qualification', label: 'Qualification' }, { key: 'jours', label: 'Jours', num: true, total: true, fmt: fmtNb },
              { key: 'heuresSup', label: 'H sup', num: true, total: true, fmt: fmtNb }, { key: 'masse', label: 'Masse (DH)', num: true, total: true, fmt: fmtMt },
            ]} />
          </Section>
        </div>
        <div className="col-12 col-xl-6">
          <Section titre="🚜 Matériels pointés">
            <Tableau rows={d.materiels} total maxHeight={460} columns={[
              { key: 'matricule', label: 'Matricule', render: r => <FicheLien type="materiel" id={r.materielId} periode={periode}>{r.matricule}</FicheLien> },
              { key: 'type', label: 'Type' }, { key: 'joursPointes', label: 'Jours', num: true },
              { key: 'heures', label: 'Heures', num: true, total: true, fmt: fmtNb }, { key: 'litres', label: 'Gasoil (L)', num: true, total: true, fmt: fmtNb },
            ]} />
          </Section>
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-xl-6">
          <Section titre="⛽ Citernes">
            <Tableau rows={d.citernes} vide="Aucune citerne active" columns={[
              { key: 'nom', label: 'Citerne', render: r => <FicheLien type="citerne" id={r.citerneId} periode={periode}>{r.nom}</FicheLien> },
              { key: 'stock', label: 'Stock (L)', num: true, fmt: (v, r) => <span style={{ color: r.bas ? '#c62828' : undefined }}>{fmtNb(v)}</span> },
              { key: 'min', label: 'Min', num: true, fmt: fmtNb }, { key: 'max', label: 'Max', num: true, fmt: fmtNb },
            ]} />
          </Section>
        </div>
        <div className="col-12 col-xl-6">
          <Section titre="🔧 Réparations">
            <Tableau rows={d.reparations} total columns={[
              { key: 'date', label: 'Date', fmt: dt },
              { key: 'matricule', label: 'Matériel', render: r => <FicheLien type="materiel" id={r.materielId} periode={periode}>{r.matricule}</FicheLien> },
              { key: 'designation', label: 'Désignation' }, { key: 'fournisseur', label: 'Fournisseur' },
              { key: 'montant', label: 'Montant (DH)', num: true, total: true, fmt: fmtMt },
            ]} />
          </Section>
        </div>
      </div>
    </>
  );
};

const FICHES = { materiel: FicheMateriel, ouvrier: FicheOuvrier, citerne: FicheCiterne, chantier: FicheChantier };
const nomFiche = (d) => d.infos.matricule || d.infos.nom || `#${d.infos.id}`;

const FichePage = () => {
  const { type, id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const start = searchParams.get('start') || '';
  const end = searchParams.get('end') || '';

  useEffect(() => {
    setD(null); setError('');
    api.get(`/dashboard/v2/fiche/${encodeURIComponent(type)}/${encodeURIComponent(id)}`, { params: { start: start || undefined, end: end || undefined } })
      .then(r => { setD(r.data); document.title = `${nomFiche(r.data)} — ShemsPaie`; })
      .catch(e => setError(e?.response?.status === 404 ? 'Fiche introuvable.' : 'Erreur lors du chargement de la fiche.'));
  }, [type, id, start, end]);

  const Composant = FICHES[type];
  if (!Composant) return <div className="container py-4"><Alert variant="warning">Type de fiche inconnu.</Alert></div>;
  if (error) return <div className="container py-4"><Alert variant="warning">{error}</Alert></div>;
  if (!d) return <div className="text-center py-5"><Spinner animation="border" size="sm" /> Chargement…</div>;

  const periode = d.periode;
  const changerDate = (k, v) => { if (v) setSearchParams({ start: periode.start, end: periode.end, [k]: v }); };

  return (
    <div className="container-fluid px-3 px-md-4 pb-5" style={{ maxWidth: 1400 }}>
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 my-3">
        <div>
          <div className="small text-muted">{TITRES[type]}</div>
          <h4 style={{ color: BROWN, fontWeight: 700, margin: 0 }}>{nomFiche(d)}</h4>
        </div>
        <div className="d-flex gap-2 align-items-end">
          <div><label className="form-label small mb-1">Du</label>
            <input type="date" className="form-control form-control-sm" value={periode.start} onChange={e => changerDate('start', e.target.value)} /></div>
          <div><label className="form-label small mb-1">Au</label>
            <input type="date" className="form-control form-control-sm" value={periode.end} onChange={e => changerDate('end', e.target.value)} /></div>
          <a href="/dashboard" className="btn btn-sm btn-outline-secondary">← Dashboard</a>
        </div>
      </div>
      <Composant d={d} periode={periode} />
    </div>
  );
};

export default FichePage;
